import { useCallback, useEffect, useRef, useState } from 'react'
import isEqual from 'lodash/isEqual'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { BehaviorEditorRef } from 'apps/aiAutomation/components/Behavior/components/BehaviorEditor'
import {
  AiAutomationBehaviorActions,
  AiAutomationBehaviorSelectors,
} from 'apps/aiAutomation/store/behavior'
import { LoaderStatus } from 'apps/aiAutomation/store/behavior/lib/types'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { AiBehaviorProfile } from 'shared/api/requests/aiBehavior/schemas'

export const useBehavior = () => {
  const dispatch = useAppDispatch()
  const generatedProfile = useAppSelector(AiAutomationBehaviorSelectors.getGeneratedProfile)
  const currentProfile = useAppSelector(AiAutomationBehaviorSelectors.getCurrentProfile)
  const isGenerating = useAppSelector(AiAutomationBehaviorSelectors.getIsGenerating)
  const loaderStatus = useAppSelector(AiAutomationBehaviorSelectors.getCurrentProfileStatus)

  const editorRef = useRef<BehaviorEditorRef>(null)
  const [isReadMode, setReadMode] = useState<boolean>(true)

  const onSaveEditButtonClick = useCallback(() => {
    if (isReadMode) {
      setReadMode(false)
      return
    }

    if (!editorRef.current) {
      return
    }

    editorRef.current.submit().then((data) => {
      if (!data) {
        return
      }

      if (!isEqual(data, currentProfile)) {
        dispatch(AiAutomationBehaviorActions.updateConfigFx(data as AiBehaviorProfile))
      }

      dispatch(AiAutomationLayoutActions.confirmChanged({ show: false }))
      setReadMode(true)
    })
  }, [isReadMode, dispatch, currentProfile])

  useEffect(() => {
    if (!isGenerating && generatedProfile !== null) {
      setReadMode(false)
    }
  }, [generatedProfile, isGenerating])

  useEffect(() => {
    dispatch(AiAutomationBehaviorActions.getConfigFx())
  }, [dispatch])

  return {
    editorRef,
    onSaveEditButtonClick,

    isReadMode,
    isLoading: loaderStatus === LoaderStatus.LOADING,

    showEmptyState:
      loaderStatus === LoaderStatus.LOADED &&
      currentProfile === null &&
      !isGenerating &&
      generatedProfile === null,
    showGenerationLoader: isGenerating,
    showEditor:
      loaderStatus !== LoaderStatus.IDLE && (currentProfile !== null || generatedProfile !== null),
  }
}
